import { setMockPianoConfig } from 'services/Piano';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import type { Head } from 'interfaces/Head';
import type { TopicSlug } from 'interfaces/Taxonomy';
import type { TopicPackage, TopicHubPage } from 'interfaces/Home';
import getTopic from 'api/Topic/getTopic';
import getTopicPosts from 'api/Topic/getTopicPosts';
import type { GetStaticProps } from 'next/types';
import { layoutCount, rankings, subMenuHardcodedChilds } from 'constants/constants';
import MostPopularRR from 'components/MostPopularRR';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { HeaderNav, MenuItemProps } from 'interfaces/navigation/Header';
import setTopicPageAdConfig from 'services/Ad/config/setTopicPageAdConfig';
import getAllTopics from 'api/Topic/getAllTopics';
import type { GetStaticPaths } from 'next';
import getFooter from 'api/Footer/getFooter';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import getMagazineBySlug from 'api/Magazine/getMagazineBySlug';
import TopicHubPages from 'pages/TopicHubPages';
import type { JSX } from 'react';
import type { RelatedRanking } from 'interfaces/content/Ranking';
import getRankingBySlug from 'api/Ranking/getRankingBySlug';
import getRankingItems from 'utils/miscUtils/rankingsHelpers';

interface TopicProps {
  mostPopular: ArticlePreview[];
  topicPackages?: TopicPackage[];
  topic: TopicHubPage;
  slug: string;
  subMenu?: MenuItemProps[];
  pageSize: number;
  pagination: PagePagination;
  sidebar?: (JSX.Element | null)[];
  relatedRankings?: RelatedRanking[];
  rankingPanel: [];
  topicPosts?: any;
}

const TopicHub = ({
  topic,
  mostPopular,
  subMenu,
  pageSize,
  pagination,
  topicPackages,
  relatedRankings,
  rankingPanel,
  topicPosts,
}: TopicProps) => {
  return (
    <TopicHubPages
      data={topic}
      topicPackages={topicPackages}
      rankingPanel={rankingPanel}
      subMenu={subMenu}
      presentedBy={{
        linkTo: topic.sponsor?.link || '',
        logo: topic.sponsor?.thumbnail || '',
        name: topic.sponsor?.name || '',
        sponsorType: topic.sponsor?.sponsorType || '',
        topicSlug: topic.slug,
      }}
      sidebar={[
        mostPopular && (
          <MostPopularRR
            articles={mostPopular}
            key='1'
          />
        ),
        <div
          className='nativo-right-tout'
          key='nativo-right-key'
        >
          <div />
        </div>,
      ]}
      pageSize={pageSize}
      pagination={pagination}
      relatedRankings={relatedRankings}
      topicPosts={topicPosts}
    />
  );
};

interface Props {
  params: {
    name: string;
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  const pageSize = layoutCount.topic;

  try {
    const { name } = context.params as { name: string };
    const { pageNum } = (context.params || {}) as { pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);
    const offset = (currentPageNum - 1) * pageSize;
    const topic = await getTopic(name, pageSize, offset);
    if (!topic) {
      throw new Error(`Topic not found for slug: ${name}`);
    }
    let topicPackages: TopicPackage[] = [];
    let packagesArticlesIds: number[] = [];

    if (topic.packages && topic.packages.length > 0) {
      topicPackages = await Promise.all(
        topic.packages.map(async (pkg) => ({
          ...pkg,
          articles: await getMagazineBySlug(pkg.slug).then((response) => response?.articles || []),
          image: pkg.image,
          link: pkg.permalink,
        })),
      );
      packagesArticlesIds = topicPackages.flatMap((pkg) => pkg.articles.map((article) => article.databaseId));
    }
    const relatedRankings = await getRankingItems(topic.rankings || []);
    const isFortune500 = name === 'fortune-500';
    const topicPosts = await getTopicPosts(name, pageSize, offset, packagesArticlesIds, isFortune500);
    const rankingPanel = await getRankingBySlug(rankings);
    const totalArticles = topicPosts.pageInfo.totalCount > 0 ? topicPosts.pageInfo.totalCount : 0;
    const pagination = calculatePagePagination(currentPageNum, totalArticles);

    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );

    const subMenu =
      menu?.topicNavigation.find((menuItem) => menuItem.url === new URL(topic.link).pathname)?.subMenu || null;
    const subMenuHardcoded =
      subMenuHardcodedChilds.find((menuItem) => menuItem.url === new URL(topic.link).pathname)?.subMenu || null;

    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const mostPopular = await safeAsync(() => getMostPopularPosts(), [] as ArticlePreview[]);

    const pageAdConfig = safe(() => setTopicPageAdConfig(topic), fallbackData.pageAdConfig);
    const dataLayer = safe(() => setPageGtmConfig(topic), fallbackData.dataLayer);
    const headData: HeadData = safe(
      () =>
        setHeadData(
          {
            ...topic,
            canonicalUrl: pageNum ? `${topic.canonicalUrl}page/${pageNum}/` : topic.canonicalUrl,
            titlePrefix: pagination.pageInfo,
          } as Head,
          true,
          'Topic',
        ),
      fallbackData.headData,
    );

    return {
      props: {
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        mostPopular,
        pageAdConfig,
        pageSize,
        pagination,
        pianoPageConfig: setMockPianoConfig(),
        slug: name,
        subMenu: subMenu || subMenuHardcoded,
        topic,
        topicPackages,
        relatedRankings,
        rankingPanel,
        topicPosts,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ name: string }> = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            name: 'cfo-daily',
          },
        },
      ],
    };
  }

  const { topics }: { topics: TopicSlug[] } = await getAllTopics();
  const paths: Props[] = topics.map(
    (topic) =>
      ({
        params: {
          name: topic.slug,
        },
      }) as Props,
  );

  return {
    fallback: 'blocking',
    paths,
  };
};

export default TopicHub;
